var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/* Reusable functions for building pending order and order details. */
import { buildRecipesMap, getMealItem, makeDeepCopy } from './common_utils';
import { isValidMealInput, isValidValue } from './form_validations';
import { DELIVERY_INTERVALS_NUMBER_MAP, FREE_SHIPPING_THRESHOLD_DOLLARS, ORDER_CHARGE_STATUS, PET_TYPE, REFUND_DESTINATIONS, } from '@variables';
var getPackagingDetails = function (packagingPrescriptions) {
    return packagingPrescriptions.reduce(function (res, curr) {
        if (res.boxSizes[curr.boxSize]) {
            res.boxSizes[curr.boxSize]++;
        }
        else {
            res.boxSizes[curr.boxSize] = 1;
        }
        if (curr.linerSize) {
            if (res.linerSizes[curr.linerSize]) {
                res.linerSizes[curr.linerSize]++;
            }
            else {
                res.linerSizes[curr.linerSize] = 1;
            }
        }
        res.dryIceLbs += curr.dryIceLbs;
        res.gelIceLbs += curr.gelIceLbs;
        return res;
    }, { boxSizes: {}, linerSizes: {}, dryIceLbs: 0, gelIceLbs: 0 });
};
var combineChargesAndBalanceRefunds = function (order) {
    var isBalanceRefund = function (refund) {
        return refund.refundDestination === REFUND_DESTINATIONS.REFUND_DESTINATION_BALANCE;
    };
    var balanceRefunds = order.orderRefunds ? order.orderRefunds.filter(isBalanceRefund) : [];
    var charges = order.charges ? makeDeepCopy(order.charges) : [];
    var chargesAndBalanceRefunds = __spreadArray(__spreadArray([], balanceRefunds, true), charges, true);
    chargesAndBalanceRefunds.sort(function (a, b) { return Date.parse(a.date) - Date.parse(b.date); });
    return chargesAndBalanceRefunds;
};
// Creates a list of pet meal buckets that have added fields from corresponding orderPetMeals.
var constructOrderPetMealBuckets = function (orderPetMeals, pets) {
    var orderPetMealBucketList = [];
    orderPetMeals.forEach(function (orderPetMeal) {
        var pet = pets.find(function (pet) { return pet.key.id === orderPetMeal.pet.id; });
        if (pet.petType === PET_TYPE.DOG) {
            orderPetMeal.mealBuckets.forEach(function (mealBucket, index) {
                orderPetMealBucketList.push({
                    grams: mealBucket.grams,
                    mealsPerDay: orderPetMeal.mealsPerDay,
                    // Only the first mealBucket gets a (T) on packing slips. See pack_label_utils.py.
                    needsTransition: index === 0 ? orderPetMeal.needsTransition : false,
                    numMeals: mealBucket.numMeals,
                    petName: orderPetMeal.name,
                    petType: pet.petType,
                    petId: orderPetMeal.pet.id,
                    recipe: orderPetMeal.recipe,
                });
            });
        }
    });
    return orderPetMealBucketList;
};
/* Validates all order and pending order input values. */
var validateOrderInputValues = function (street, city, state, zipcode, name, isAdmin, cookDate, orderPetMeals, orderItems, recipes) {
    var recipesMap = buildRecipesMap(recipes);
    return {
        street: isValidValue('street', street),
        city: isValidValue('city', city),
        state: isValidValue('state', state),
        zipcode: isValidValue('zipcode', zipcode),
        name: isValidValue('customerName', name, isAdmin),
        cookDate: isValidValue('cookDate', cookDate),
        meals: orderPetMeals.every(function (orderPetMeal) { return isValidMealInput(orderPetMeal.meals); }),
        orderPetMeals: isValidValue('orderPetMeals', orderPetMeals),
        items: isValidValue('items', orderItems),
        recipes: orderPetMeals.every(function (orderPetMeal) { return recipesMap[orderPetMeal.recipe.name].deleted === false; }),
    };
};
var isInstanceOfPendingOrder = function (order) {
    return 'isLocked' in order;
};
/* Determine whether an order was charged. */
var isOrderCharged = function (order) {
    var _a;
    if (order.lastChargeStatus === ORDER_CHARGE_STATUS.SUCCEEDED) {
        return true;
    }
    if ((_a = order.charges) === null || _a === void 0 ? void 0 : _a.find(function (charge) { return charge.status === ORDER_CHARGE_STATUS.SUCCEEDED; })) {
        return true;
    }
    return false;
};
/**
 * Only non-trial PendingOrders with a previous Order that has been charged can be edited.
 * An OTTO customer converting to a subscription would have their PendingOrder considered as a
 * trial order and should not be editable.
 */
var isPendingOrderEditable = function (pendingOrder, previousOrder) {
    return !pendingOrder.isTrialOrder && !!previousOrder && isOrderCharged(previousOrder);
};
/** Under certain conditions, depending on the cost model, the SKU plan
 * calculator includes shipping cost in its total for subscription meals. On
 * plans that reveal their prices (i.e. Flex) customers expect to see the price
 * they were shown, so we need to pull out the shipping cost as a separate line
 * item. This function determines when we should do so. */
var shouldSeparateOutShippingCost = function (subscriptionMealsCost, isOnFlexibleMealPlan) {
    return subscriptionMealsCost < FREE_SHIPPING_THRESHOLD_DOLLARS && isOnFlexibleMealPlan;
};
/** Gets the cost per week based on Customer's pending order */
var getPlanCostPerWeek = function (items, deliveryInterval) {
    var subscriptionMeals = getMealItem(items);
    return subscriptionMeals.costPerUnit / DELIVERY_INTERVALS_NUMBER_MAP[deliveryInterval];
};
/** Gets the customer's trial order */
var getCustomerTrialOrder = function (orders) {
    return orders.find(function (order) { return !order.deleted && order.isFirstOrder && order.isTrialOrder; });
};
export { combineChargesAndBalanceRefunds, constructOrderPetMealBuckets, getCustomerTrialOrder, getPackagingDetails, getPlanCostPerWeek, isInstanceOfPendingOrder, isOrderCharged, isPendingOrderEditable, shouldSeparateOutShippingCost, validateOrderInputValues, };
